import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { 
  Loader2, Save, Mail, Settings, Send, Truck, Plus, Edit, Trash2, 
  X, Check, RefreshCw, Link as LinkIcon, Upload, List, CheckCircle, Users, UserPlus, Copy,
  Database, Download, HardDrive, Clock, FileJson, AlertTriangle, Package
} from 'lucide-react';
import { colors } from '../theme';

const ConfiguracoesUnificadas = ({ user }) => {
  // Tab ativa
  const [activeTab, setActiveTab] = useState('transportadoras');
  
  // Estados para Transportadoras
  const [transportadoras, setTransportadoras] = useState([]);
  const [loadingTransp, setLoadingTransp] = useState(false);
  const [showTranspForm, setShowTranspForm] = useState(false);
  const [editingTransp, setEditingTransp] = useState(null);
  const [transpForm, setTranspForm] = useState({ nome: '', url_rastreamento: '' });
  const [savingTransp, setSavingTransp] = useState(false);
  const [buscaTransp, setBuscaTransp] = useState('');
  
  // Estados para Email
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');
  const [emailError, setEmailError] = useState('');
  const [testSuccess, setTestSuccess] = useState('');
  const [testError, setTestError] = useState('');
  const [emailConfig, setEmailConfig] = useState({
    smtp_server: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    email_remetente: '',
    notificacoes_ativas: false,
    template_faturado: '',
    template_em_transito: '',
    template_entregue: ''
  });

  // Estados para API Externa
  const [loadingApi, setLoadingApi] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [systemConfig, setSystemConfig] = useState({
    api_url: '',
    api_token: '',
    auto_refresh_active: false,
    auto_refresh_interval: 5
  });

  // Estados para Status de Pedidos
  const [statusList, setStatusList] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [showStatusForm, setShowStatusForm] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [statusForm, setStatusForm] = useState({
    nome: '',
    cor: '#6B7280',
    ordem: 0,
    ativo: true,
    is_final: false
  });

  // Estados para Usuários
  const [usuarios, setUsuarios] = useState([]);
  const [usuariosFiltrados, setUsuariosFiltrados] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [buscaUser, setBuscaUser] = useState('');
  const [filtroRole, setFiltroRole] = useState('TODOS');
  const [copiado, setCopiado] = useState(null);
  const [userForm, setUserForm] = useState({
    username: '',
    name: '',
    password: '',
    role: 'vendedor'
  });

  // Estados para Backup
  const [backupInfo, setBackupInfo] = useState(null);
  const [backupHistory, setBackupHistory] = useState([]);
  const [loadingBackup, setLoadingBackup] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  const [backupProgress, setBackupProgress] = useState(0);
  const [backupStatusMsg, setBackupStatusMsg] = useState('');
  const [restoringBackup, setRestoringBackup] = useState(false);
  const [backupSuccess, setBackupSuccess] = useState('');
  const [backupError, setBackupError] = useState('');

  // Estados para Caixas Padrão
  const [caixasPadrao, setCaixasPadrao] = useState([]);
  const [loadingCaixas, setLoadingCaixas] = useState(false);
  const [showCaixaForm, setShowCaixaForm] = useState(false);
  const [editingCaixa, setEditingCaixa] = useState(null);
  const [savingCaixa, setSavingCaixa] = useState(false);
  const [caixaForm, setCaixaForm] = useState({
    nome: '',
    comprimento: '',
    largura: '',
    altura: ''
  });
  useEffect(() => {
    loadTransportadoras();
    loadEmailConfig();
    loadSystemConfig();
    loadStatusList();
    loadUsuarios();
    loadBackupInfo();
    loadCaixasPadrao();
  }, []);

  // Efeito para filtrar usuários
  useEffect(() => {
    let filtrados = [...usuarios];
    if (filtroRole !== 'TODOS') {
      filtrados = filtrados.filter(u => u.role === filtroRole);
    }
    if (buscaUser.trim()) {
      const termoLower = buscaUser.toLowerCase();
      filtrados = filtrados.filter(u =>
        u.name.toLowerCase().includes(termoLower) ||
        u.username.toLowerCase().includes(termoLower)
      );
    }
    setUsuariosFiltrados(filtrados);
  }, [buscaUser, filtroRole, usuarios]);

  // ========== FUNÇÕES TRANSPORTADORAS ==========
  const loadTransportadoras = async () => {
    setLoadingTransp(true);
    try {
      const response = await axios.get(`${API}/transportadoras`);
      setTransportadoras(response.data.sort((a, b) => a.nome.localeCompare(b.nome)));
    } catch (error) {
      console.error('Erro ao carregar transportadoras:', error);
    } finally {
      setLoadingTransp(false);
    }
  };

  const handleSaveTransp = async () => {
    if (!transpForm.nome.trim()) return;
    
    setSavingTransp(true);
    try {
      const token = localStorage.getItem('primaza_token');
      if (editingTransp) {
        await axios.put(`${API}/transportadoras/${editingTransp}`, transpForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/transportadoras`, { ...transpForm, ativa: true }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      await loadTransportadoras();
      setShowTranspForm(false);
      setEditingTransp(null);
      setTranspForm({ nome: '', url_rastreamento: '' });
    } catch (error) {
      console.error('Erro ao salvar transportadora:', error);
    } finally {
      setSavingTransp(false);
    }
  };

  const handleDeleteTransp = async (id) => {
    if (!window.confirm('Deseja excluir esta transportadora?')) return;
    
    try {
      const token = localStorage.getItem('primaza_token');
      await axios.delete(`${API}/transportadoras/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadTransportadoras();
    } catch (error) {
      console.error('Erro ao excluir transportadora:', error);
    }
  };

  const handleLimparDuplicadas = async () => {
    if (!window.confirm('Isso irá remover transportadoras com nomes duplicados e atualizar os pedidos. Deseja continuar?')) return;
    
    setLoadingTransp(true);
    try {
      const token = localStorage.getItem('primaza_token');
      const response = await axios.post(`${API}/admin/limpar-transportadoras-duplicadas`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { transportadoras_removidas, pedidos_atualizados } = response.data;
      alert(`✅ Limpeza concluída!\n\n- ${transportadoras_removidas} transportadoras removidas\n- ${pedidos_atualizados} pedidos atualizados`);
      await loadTransportadoras();
    } catch (error) {
      console.error('Erro ao limpar duplicadas:', error);
      alert('❌ Erro ao limpar duplicadas: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingTransp(false);
    }
  };

  const handleEditTransp = (transp) => {
    setEditingTransp(transp.id);
    setTranspForm({ nome: transp.nome, url_rastreamento: transp.url_rastreamento || '' });
    setShowTranspForm(true);
  };

  const transportadorasFiltradas = transportadoras.filter(t =>
    t.nome.toLowerCase().includes(buscaTransp.toLowerCase())
  );

  // ========== FUNÇÕES EMAIL ==========
  const loadEmailConfig = async () => {
    setLoadingEmail(true);
    try {
      const token = localStorage.getItem('primaza_token');
      const response = await axios.get(`${API}/config/email`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailConfig(response.data);
    } catch (err) {
      console.error('Erro ao carregar configurações de email:', err);
    } finally {
      setLoadingEmail(false);
    }
  };

  const handleSaveEmail = async () => {
    setSavingEmail(true);
    setEmailError('');
    setEmailSuccess('');

    try {
      const token = localStorage.getItem('primaza_token');
      await axios.post(`${API}/config/email`, emailConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailSuccess('Configurações salvas com sucesso!');
      setTimeout(() => setEmailSuccess(''), 3000);
    } catch (err) {
      setEmailError(err.response?.data?.detail || 'Erro ao salvar configurações');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    setTestError('');
    setTestSuccess('');

    try {
      const token = localStorage.getItem('primaza_token');
      await axios.post(`${API}/config/email/test`, emailConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestSuccess('E-mail de teste enviado com sucesso! Verifique sua caixa de entrada.');
      setTimeout(() => setTestSuccess(''), 5000);
    } catch (err) {
      setTestError(err.response?.data?.detail || 'Erro ao enviar e-mail de teste');
    } finally {
      setTestingEmail(false);
    }
  };

  // ========== FUNÇÕES API EXTERNA ==========
  const loadSystemConfig = async () => {
    try {
      const response = await axios.get(`${API}/config/system`);
      setSystemConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar configuração do sistema:', error);
    }
  };

  const handleSaveSystemConfig = async () => {
    setLoadingApi(true);
    try {
      const token = localStorage.getItem('primaza_token');
      await axios.put(`${API}/config/system`, systemConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('✅ Configuração salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      alert('❌ Erro ao salvar configuração');
    } finally {
      setLoadingApi(false);
    }
  };

  const testApiConnection = async () => {
    if (!systemConfig.api_url?.trim()) {
      alert('Configure a URL da API primeiro!');
      return;
    }

    setLoadingApi(true);
    try {
      const token = localStorage.getItem('primaza_token');
      const response = await axios.post(`${API}/test-external-api`, {
        url: systemConfig.api_url.trim(),
        token: systemConfig.api_token || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`✅ Conexão com API bem-sucedida!\n\n📡 Status: ${response.data.status_code}\n💡 API está respondendo corretamente.`);
    } catch (error) {
      console.error('Erro:', error);
      alert('❌ Erro ao testar API: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingApi(false);
    }
  };

  const importFromApi = async () => {
    if (!systemConfig.api_url?.trim()) {
      alert('Configure a URL da API primeiro!');
      return;
    }

    setLoadingApi(true);
    try {
      const token = localStorage.getItem('primaza_token');
      const response = await axios.post(`${API}/import-from-api`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const now = new Date().toLocaleString();
      setLastUpdate(now);
      alert(`✅ Dados importados da API com sucesso!\n\n📊 Total de registros: ${response.data.total_records}\n✅ Importados: ${response.data.imported_records}`);
    } catch (error) {
      console.error('Erro:', error);
      alert('❌ Erro ao importar da API: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingApi(false);
    }
  };

  // ========== FUNÇÕES STATUS DE PEDIDOS ==========
  const loadStatusList = async () => {
    setLoadingStatus(true);
    try {
      const token = localStorage.getItem('primaza_token');
      const response = await axios.get(`${API}/status-pedido`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatusList(response.data);
    } catch (error) {
      console.error('Erro ao carregar status:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleSaveStatus = async () => {
    if (!statusForm.nome.trim()) return;
    
    setLoadingStatus(true);
    try {
      const token = localStorage.getItem('primaza_token');
      if (editingStatus) {
        await axios.put(`${API}/status-pedido/${editingStatus}`, statusForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/status-pedido`, statusForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      await loadStatusList();
      setShowStatusForm(false);
      setEditingStatus(null);
      setStatusForm({ nome: '', cor: '#6B7280', ordem: 0, ativo: true, is_final: false });
    } catch (error) {
      console.error('Erro ao salvar status:', error);
      alert('Erro ao salvar status');
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleEditStatus = (status) => {
    setStatusForm({
      nome: status.nome,
      cor: status.cor,
      ordem: status.ordem,
      ativo: status.ativo,
      is_final: status.is_final
    });
    setEditingStatus(status.id);
    setShowStatusForm(true);
  };

  const handleDeleteStatus = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este status?')) return;
    
    setLoadingStatus(true);
    try {
      const token = localStorage.getItem('primaza_token');
      await axios.delete(`${API}/status-pedido/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadStatusList();
    } catch (error) {
      console.error('Erro ao deletar status:', error);
      alert('Erro ao deletar status');
    } finally {
      setLoadingStatus(false);
    }
  };

  // ========== FUNÇÕES USUÁRIOS ==========
  const loadUsuarios = async () => {
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('primaza_token');
      const response = await axios.get(`${API}/auth/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const usuariosList = response.data.sort((a, b) => a.name.localeCompare(b.name));
      setUsuarios(usuariosList);
      setUsuariosFiltrados(usuariosList);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSaveUser = async () => {
    if (!userForm.username.trim() || !userForm.name.trim()) {
      alert('Preencha usuário e nome');
      return;
    }
    if (!editingUser && !userForm.password.trim()) {
      alert('Preencha a senha para criar novo usuário');
      return;
    }

    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('primaza_token');
      if (editingUser) {
        const dataToUpdate = {
          username: userForm.username,
          name: userForm.name,
          role: userForm.role
        };
        if (userForm.password.trim()) {
          dataToUpdate.password = userForm.password;
        }
        await axios.put(`${API}/auth/users/${editingUser}`, dataToUpdate, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('✅ Usuário atualizado com sucesso!');
      } else {
        await axios.post(`${API}/auth/register`, userForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('✅ Usuário criado com sucesso!');
      }
      await loadUsuarios();
      setShowUserForm(false);
      setEditingUser(null);
      setUserForm({ username: '', name: '', password: '', role: 'vendedor' });
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      alert('❌ Erro ao salvar usuário: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleEditUser = (usuario) => {
    setUserForm({
      username: usuario.username,
      name: usuario.name,
      password: '',
      role: usuario.role
    });
    setEditingUser(usuario.id);
    setShowUserForm(true);
  };

  const handleDeleteUser = async (id, name) => {
    if (!window.confirm(`Tem certeza que deseja excluir o usuário "${name}"?`)) return;
    
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('primaza_token');
      await axios.delete(`${API}/auth/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('✅ Usuário excluído com sucesso!');
      await loadUsuarios();
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      alert('❌ Erro ao excluir usuário: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingUsers(false);
    }
  };

  const contarPorRole = (role) => usuarios.filter(u => u.role === role).length;

  const getRoleBadge = (role) => {
    const badges = {
      admin: { bg: 'bg-red-100', text: 'text-red-700', label: 'Admin' },
      lancador: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Lançador' },
      vendedor: { bg: 'bg-green-100', text: 'text-green-700', label: 'Vendedor' }
    };
    const badge = badges[role] || badges.vendedor;
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const copiarCredenciais = (usuario) => {
    const url = window.location.origin;
    const senha = usuario.senha_inicial || '(senha não disponível)';
    const texto = `Bem vindo ao Sistema Primaza Rastreamento

Acesse o Link: ${url}

Login: ${usuario.username}
Senha: ${senha}

Primaza sempre junto com seu cliente Rastreamento além de Brincos!`;
    navigator.clipboard.writeText(texto);
    setCopiado(usuario.id);
    setTimeout(() => setCopiado(null), 2000);
  };

  // ========== FUNÇÕES BACKUP ==========
  const loadBackupInfo = async () => {
    setLoadingBackup(true);
    try {
      const token = localStorage.getItem('primaza_token');
      const [infoRes, historyRes] = await Promise.all([
        axios.get(`${API}/backup/info`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/backup/history`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setBackupInfo(infoRes.data);
      setBackupHistory(historyRes.data);
    } catch (error) {
      console.error('Erro ao carregar info de backup:', error);
    } finally {
      setLoadingBackup(false);
    }
  };

  const handleDownloadBackup = async () => {
    setDownloadingBackup(true);
    setBackupError('');
    setBackupSuccess('');
    setBackupProgress(0);
    setBackupStatusMsg('Iniciando backup...');
    try {
      const token = localStorage.getItem('primaza_token');
      const headers = { Authorization: `Bearer ${token}` };

      // 1. Iniciar backup em background
      let startRes;
      try {
        startRes = await axios.post(`${API}/backup/start`, {}, { headers });
      } catch (err) {
        if (err.response && err.response.status === 404) {
          startRes = await axios.post(`${API}/backup/download`, {}, { headers });
        } else {
          throw err;
        }
      }
      const taskId = startRes.data.task_id;
      setBackupStatusMsg('Gerando backup...');

      // 2. Polling de status — tolerante a erros transitórios (520, rede)
      let status = 'pending';
      let attempts = 0;
      let consecutiveErrors = 0;
      const maxAttempts = 150;      // 150 x 3s = 7.5 minutos
      const maxConsecErrors = 10;   // até 10 erros seguidos antes de desistir
      while (status !== 'done' && status !== 'error' && attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 3000));
        attempts++;
        try {
          const statusRes = await axios.get(`${API}/backup/status/${taskId}`, { headers, timeout: 10000 });
          consecutiveErrors = 0;
          status = statusRes.data.status;
          setBackupProgress(statusRes.data.progress || 0);
          if (status === 'processing') {
            setBackupStatusMsg(`Exportando coleções... ${statusRes.data.progress}%`);
          } else if (status === 'done') {
            setBackupStatusMsg('Download pronto!');
          } else if (status === 'error') {
            throw new Error(statusRes.data.error || 'Erro ao gerar backup');
          }
        } catch (pollErr) {
          consecutiveErrors++;
          console.warn(`Erro no polling (${consecutiveErrors}/${maxConsecErrors})...`, pollErr.message);
          if (consecutiveErrors >= maxConsecErrors) {
            throw new Error('Servidor sem resposta. Tente o botão "Backup Direto".');
          }
          // Erro transitório (520, rede) — continuar tentando
        }
      }

      if (status !== 'done') {
        throw new Error('Timeout aguardando backup. Tente o botão "Backup Direto".');
      }

      // 3. Baixar arquivo pronto
      setBackupStatusMsg('Baixando arquivo...');
      const downloadRes = await axios.get(`${API}/backup/download/${taskId}`, {
        headers,
        responseType: 'blob',
        timeout: 300000
      });

      const url = window.URL.createObjectURL(new Blob([downloadRes.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.setAttribute('download', `backup_primaza_${timestamp}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setBackupSuccess('Backup baixado com sucesso!');
      loadBackupInfo();
    } catch (error) {
      console.error('Erro ao baixar backup:', error);
      setBackupError(error.message || 'Erro ao gerar backup. Tente o "Backup Direto" abaixo.');
    } finally {
      setDownloadingBackup(false);
      setBackupProgress(0);
      setBackupStatusMsg('');
    }
  };

  // Fallback: backup direto (síncrono, sem background task)
  const handleBackupDireto = async () => {
    setDownloadingBackup(true);
    setBackupError('');
    setBackupSuccess('');
    setBackupStatusMsg('Gerando backup direto (pode levar alguns segundos)...');
    setBackupProgress(50);
    try {
      const token = localStorage.getItem('primaza_token');
      const downloadRes = await axios.get(`${API}/backup/download-direto`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 300000
      });

      const url = window.URL.createObjectURL(new Blob([downloadRes.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.setAttribute('download', `backup_primaza_${timestamp}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setBackupSuccess('Backup baixado com sucesso!');
      loadBackupInfo();
    } catch (error) {
      console.error('Erro no backup direto:', error);
      setBackupError(error.response?.data?.detail || 'Erro ao gerar backup direto.');
    } finally {
      setDownloadingBackup(false);
      setBackupProgress(0);
      setBackupStatusMsg('');
    }
  };

  const handleRestoreBackup = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!window.confirm('ATENÇÃO: Restaurar um backup irá substituir todos os dados atuais (exceto usuários). Deseja continuar?')) {
      event.target.value = '';
      return;
    }
    
    setRestoringBackup(true);
    setBackupError('');
    setBackupSuccess('');
    
    try {
      const token = localStorage.getItem('primaza_token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/backup/restore`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setBackupSuccess(`Backup restaurado! ${response.data.total_documents} documentos restaurados.`);
      loadBackupInfo();
    } catch (error) {
      console.error('Erro ao restaurar backup:', error);
      setBackupError(error.response?.data?.detail || 'Erro ao restaurar backup.');
    } finally {
      setRestoringBackup(false);
      event.target.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // ========== FUNÇÕES CAIXAS PADRÃO ==========
  const loadCaixasPadrao = async () => {
    setLoadingCaixas(true);
    try {
      const token = localStorage.getItem('primaza_token');
      const response = await axios.get(`${API}/caixas-padrao`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCaixasPadrao(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar caixas:', error);
    } finally {
      setLoadingCaixas(false);
    }
  };

  const handleSaveCaixa = async () => {
    if (!caixaForm.nome.trim() || !caixaForm.comprimento || !caixaForm.largura || !caixaForm.altura) {
      return;
    }
    
    setSavingCaixa(true);
    try {
      const token = localStorage.getItem('primaza_token');
      const payload = {
        nome: caixaForm.nome.trim(),
        comprimento: parseFloat(caixaForm.comprimento),
        largura: parseFloat(caixaForm.largura),
        altura: parseFloat(caixaForm.altura)
      };
      
      if (editingCaixa) {
        await axios.put(`${API}/caixas-padrao/${editingCaixa}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/caixas-padrao`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      await loadCaixasPadrao();
      setShowCaixaForm(false);
      setEditingCaixa(null);
      setCaixaForm({ nome: '', comprimento: '', largura: '', altura: '' });
    } catch (error) {
      console.error('Erro ao salvar caixa:', error);
    } finally {
      setSavingCaixa(false);
    }
  };

  const handleDeleteCaixa = async (id) => {
    if (!window.confirm('Deseja excluir esta caixa?')) return;
    
    try {
      const token = localStorage.getItem('primaza_token');
      await axios.delete(`${API}/caixas-padrao/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadCaixasPadrao();
    } catch (error) {
      console.error('Erro ao excluir caixa:', error);
    }
  };

  const handleSeedCaixas = async () => {
    try {
      const token = localStorage.getItem('primaza_token');
      const response = await axios.post(`${API}/caixas-padrao/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadCaixasPadrao();
      alert(response.data.message);
    } catch (error) {
      console.error('Erro ao popular caixas:', error);
      alert('Erro ao popular caixas padrão');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-6 mb-6 rounded-lg shadow-sm">
        <div className="flex items-center gap-3">
          <Settings size={32} style={{ color: colors.primaza.primary }} />
          <div>
            <h1 className="text-3xl font-bold" style={{ color: colors.primaza.secondary }}>
              Configurações
            </h1>
            <p className="text-gray-600 mt-1">
              Gerenciamento de transportadoras, status, API e e-mail
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button
            variant={activeTab === 'transportadoras' ? 'default' : 'outline'}
            onClick={() => setActiveTab('transportadoras')}
            style={activeTab === 'transportadoras' ? { backgroundColor: colors.primaza.primary } : {}}
          >
            <Truck size={18} className="mr-2" />
            Transportadoras ({transportadoras.length})
          </Button>
          <Button
            variant={activeTab === 'status' ? 'default' : 'outline'}
            onClick={() => setActiveTab('status')}
            style={activeTab === 'status' ? { backgroundColor: colors.primaza.primary } : {}}
          >
            <List size={18} className="mr-2" />
            Status ({statusList.length})
          </Button>
          <Button
            variant={activeTab === 'usuarios' ? 'default' : 'outline'}
            onClick={() => setActiveTab('usuarios')}
            style={activeTab === 'usuarios' ? { backgroundColor: colors.primaza.primary } : {}}
          >
            <Users size={18} className="mr-2" />
            Usuários ({usuarios.length})
          </Button>
          <Button
            variant={activeTab === 'api' ? 'default' : 'outline'}
            onClick={() => setActiveTab('api')}
            style={activeTab === 'api' ? { backgroundColor: colors.primaza.primary } : {}}
          >
            <Settings size={18} className="mr-2" />
            API Externa
          </Button>
          <Button
            variant={activeTab === 'email' ? 'default' : 'outline'}
            onClick={() => setActiveTab('email')}
            style={activeTab === 'email' ? { backgroundColor: colors.primaza.primary } : {}}
          >
            <Mail size={18} className="mr-2" />
            E-mail
          </Button>
          <Button
            variant={activeTab === 'backup' ? 'default' : 'outline'}
            onClick={() => setActiveTab('backup')}
            style={activeTab === 'backup' ? { backgroundColor: colors.primaza.primary } : {}}
          >
            <Database size={18} className="mr-2" />
            Backup
          </Button>
          <Button
            variant={activeTab === 'caixas' ? 'default' : 'outline'}
            onClick={() => setActiveTab('caixas')}
            style={activeTab === 'caixas' ? { backgroundColor: colors.primaza.primary } : {}}
          >
            <Package size={18} className="mr-2" />
            Caixas ({caixasPadrao.length})
          </Button>
        </div>

        {/* Tab Content: Transportadoras */}
        {activeTab === 'transportadoras' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="flex items-center gap-2">
                  <Truck className="h-5 w-5" />
                  Transportadoras Cadastradas
                </CardTitle>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLimparDuplicadas}
                    disabled={loadingTransp}
                    className="text-orange-600 border-orange-300 hover:bg-orange-50"
                  >
                    <Trash2 size={16} className="mr-2" />
                    Limpar Duplicadas
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadTransportadoras}
                    disabled={loadingTransp}
                  >
                    <RefreshCw size={16} className={`mr-2 ${loadingTransp ? 'animate-spin' : ''}`} />
                    Atualizar
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      setShowTranspForm(true);
                      setEditingTransp(null);
                      setTranspForm({ nome: '', url_rastreamento: '' });
                    }}
                    style={{ backgroundColor: colors.primaza.primary }}
                  >
                    <Plus size={16} className="mr-2" />
                    Nova Transportadora
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Formulário de Transportadora */}
              {showTranspForm && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
                  <h3 className="font-semibold mb-4">
                    {editingTransp ? 'Editar Transportadora' : 'Nova Transportadora'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Nome da Transportadora *</Label>
                      <Input
                        value={transpForm.nome}
                        onChange={(e) => setTranspForm({ ...transpForm, nome: e.target.value })}
                        placeholder="Ex: Correios, Jadlog..."
                      />
                    </div>
                    <div>
                      <Label>URL de Rastreamento</Label>
                      <Input
                        value={transpForm.url_rastreamento}
                        onChange={(e) => setTranspForm({ ...transpForm, url_rastreamento: e.target.value })}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button
                      onClick={handleSaveTransp}
                      disabled={savingTransp || !transpForm.nome.trim()}
                      style={{ backgroundColor: colors.primaza.primary }}
                    >
                      {savingTransp ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Check size={16} className="mr-2" />
                      )}
                      Salvar
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowTranspForm(false);
                        setEditingTransp(null);
                        setTranspForm({ nome: '', url_rastreamento: '' });
                      }}
                    >
                      <X size={16} className="mr-2" />
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}

              {/* Busca */}
              <div className="mb-4">
                <Input
                  placeholder="🔍 Buscar transportadora..."
                  value={buscaTransp}
                  onChange={(e) => setBuscaTransp(e.target.value)}
                />
                <p className="text-sm text-gray-500 mt-1">
                  {transportadorasFiltradas.length} de {transportadoras.length} transportadoras
                </p>
              </div>

              {/* Lista */}
              {loadingTransp ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {transportadorasFiltradas.map((transp) => (
                    <div
                      key={transp.id}
                      className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex-1">
                        <span className="font-medium">{transp.nome}</span>
                        {transp.url_rastreamento && (
                          <div className="flex items-center gap-1 text-sm text-blue-600">
                            <LinkIcon size={12} />
                            <a href={transp.url_rastreamento} target="_blank" rel="noopener noreferrer" className="hover:underline truncate max-w-xs">
                              {transp.url_rastreamento}
                            </a>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditTransp(transp)}
                        >
                          <Edit size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteTransp(transp.id)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tab Content: Email */}
        {activeTab === 'email' && (
          <div className="space-y-6">
            {loadingEmail ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : (
              <>
                {/* Configurações SMTP */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Mail className="h-5 w-5" />
                      Configurações SMTP
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Servidor SMTP</Label>
                        <Input
                          value={emailConfig.smtp_server}
                          onChange={(e) => setEmailConfig({ ...emailConfig, smtp_server: e.target.value })}
                          placeholder="smtp.gmail.com"
                        />
                      </div>
                      <div>
                        <Label>Porta</Label>
                        <Input
                          type="number"
                          value={emailConfig.smtp_port}
                          onChange={(e) => setEmailConfig({ ...emailConfig, smtp_port: parseInt(e.target.value) })}
                          placeholder="587"
                        />
                      </div>
                      <div>
                        <Label>Usuário/E-mail</Label>
                        <Input
                          value={emailConfig.smtp_user}
                          onChange={(e) => setEmailConfig({ ...emailConfig, smtp_user: e.target.value })}
                          placeholder="seu@email.com"
                        />
                      </div>
                      <div>
                        <Label>Senha</Label>
                        <Input
                          type="password"
                          value={emailConfig.smtp_password}
                          onChange={(e) => setEmailConfig({ ...emailConfig, smtp_password: e.target.value })}
                          placeholder="••••••••"
                        />
                      </div>
                      <div>
                        <Label>E-mail Remetente</Label>
                        <Input
                          value={emailConfig.email_remetente}
                          onChange={(e) => setEmailConfig({ ...emailConfig, email_remetente: e.target.value })}
                          placeholder="noreply@empresa.com"
                        />
                      </div>
                      <div className="flex items-center gap-2 pt-6">
                        <input
                          type="checkbox"
                          id="notificacoes"
                          checked={emailConfig.notificacoes_ativas}
                          onChange={(e) => setEmailConfig({ ...emailConfig, notificacoes_ativas: e.target.checked })}
                          className="h-4 w-4"
                        />
                        <Label htmlFor="notificacoes" className="cursor-pointer">
                          Notificações Ativas
                        </Label>
                      </div>
                    </div>

                    <div className="flex gap-2 mt-6">
                      <Button
                        onClick={handleSaveEmail}
                        disabled={savingEmail}
                        style={{ backgroundColor: colors.primaza.primary }}
                      >
                        {savingEmail ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Save size={16} className="mr-2" />
                        )}
                        Salvar Configurações
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleTestEmail}
                        disabled={testingEmail}
                      >
                        {testingEmail ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Send size={16} className="mr-2" />
                        )}
                        Enviar E-mail de Teste
                      </Button>
                    </div>

                    {emailSuccess && (
                      <Alert className="mt-4 bg-green-50 border-green-200">
                        <AlertDescription className="text-green-700">{emailSuccess}</AlertDescription>
                      </Alert>
                    )}
                    {emailError && (
                      <Alert className="mt-4 bg-red-50 border-red-200">
                        <AlertDescription className="text-red-700">{emailError}</AlertDescription>
                      </Alert>
                    )}
                    {testSuccess && (
                      <Alert className="mt-4 bg-blue-50 border-blue-200">
                        <AlertDescription className="text-blue-700">{testSuccess}</AlertDescription>
                      </Alert>
                    )}
                    {testError && (
                      <Alert className="mt-4 bg-red-50 border-red-200">
                        <AlertDescription className="text-red-700">{testError}</AlertDescription>
                      </Alert>
                    )}
                  </CardContent>
                </Card>

                {/* Templates de E-mail */}
                <Card>
                  <CardHeader>
                    <CardTitle>Templates de E-mail</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Template - Pedido Faturado</Label>
                      <Textarea
                        value={emailConfig.template_faturado}
                        onChange={(e) => setEmailConfig({ ...emailConfig, template_faturado: e.target.value })}
                        placeholder="Olá {nome_cliente}, seu pedido {pedido} foi faturado..."
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>Template - Em Trânsito</Label>
                      <Textarea
                        value={emailConfig.template_em_transito}
                        onChange={(e) => setEmailConfig({ ...emailConfig, template_em_transito: e.target.value })}
                        placeholder="Olá {nome_cliente}, seu pedido {pedido} está em trânsito..."
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>Template - Entregue</Label>
                      <Textarea
                        value={emailConfig.template_entregue}
                        onChange={(e) => setEmailConfig({ ...emailConfig, template_entregue: e.target.value })}
                        placeholder="Olá {nome_cliente}, seu pedido {pedido} foi entregue..."
                        rows={3}
                      />
                    </div>
                    <p className="text-sm text-gray-500">
                      Variáveis disponíveis: {'{nome_cliente}'}, {'{pedido}'}, {'{nfe}'}, {'{transportadora}'}, {'{cod_rastreamento}'}
                    </p>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Tab Content: API Externa */}
        {activeTab === 'api' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: colors.primaza.secondary }}>
                  <Settings className="h-5 w-5" />
                  Configuração de API Externa
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>URL da API *</Label>
                  <Input
                    placeholder="https://api.exemplo.com/pedidos"
                    value={systemConfig.api_url || ''}
                    onChange={(e) => setSystemConfig({...systemConfig, api_url: e.target.value})}
                    className="mt-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    URL completa da API que retorna os pedidos em formato JSON
                  </p>
                </div>
                
                <div>
                  <Label>Token de Autenticação (opcional)</Label>
                  <Input
                    type="password"
                    placeholder="Bearer token ou API key"
                    value={systemConfig.api_token || ''}
                    onChange={(e) => setSystemConfig({...systemConfig, api_token: e.target.value})}
                    className="mt-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Token será enviado no header Authorization
                  </p>
                </div>
                
                <div 
                  className="flex items-center space-x-4 p-4 rounded-lg border"
                  style={{
                    backgroundColor: colors.primaza.primaryLight,
                    borderColor: colors.primaza.primary
                  }}
                >
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={systemConfig.auto_refresh_active}
                      onCheckedChange={(checked) => setSystemConfig({...systemConfig, auto_refresh_active: checked})}
                    />
                    <Label>Auto-refresh global ativo</Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Label>Intervalo:</Label>
                    <select 
                      value={systemConfig.auto_refresh_interval}
                      onChange={(e) => setSystemConfig({...systemConfig, auto_refresh_interval: parseInt(e.target.value)})}
                      className="px-3 py-1 border rounded"
                    >
                      <option value={5}>5 min</option>
                      <option value={10}>10 min</option>
                      <option value={30}>30 min</option>
                      <option value={60}>60 min</option>
                    </select>
                  </div>
                </div>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-800 mb-2">Formato esperado da API:</h4>
                  <pre className="text-xs text-yellow-700 bg-yellow-100 p-3 rounded overflow-x-auto">
{`[
  {
    "pedido": "39702",
    "vendedor": "GUILHERME LANG",
    "nfe": "12345",
    "status_pedido": "FATURADO",
    "prev_entrega": "2025-11-20",
    "transportadora": "Correios",
    "cod_rastreamento": "AB123456BR"
  }
]`}
                  </pre>
                </div>
                
                <div className="flex gap-3 flex-wrap">
                  <Button 
                    variant="outline"
                    onClick={testApiConnection}
                    disabled={loadingApi || !systemConfig.api_url}
                    style={{ 
                      borderColor: colors.primaza.primary,
                      color: colors.primaza.secondary 
                    }}
                  >
                    {loadingApi ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Settings className="h-4 w-4 mr-2" />
                    )}
                    Testar Conexão
                  </Button>
                  
                  <Button 
                    onClick={importFromApi} 
                    disabled={loadingApi || !systemConfig.api_url}
                    style={{
                      backgroundColor: colors.primaza.primary,
                      color: 'white'
                    }}
                  >
                    {loadingApi ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    Importar Dados da API
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={handleSaveSystemConfig}
                    disabled={loadingApi}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Salvar Configuração
                  </Button>
                </div>

                {lastUpdate && (
                  <Alert className="bg-green-50 border-green-200">
                    <AlertDescription className="text-green-700">
                      ✅ <strong>Última importação:</strong> {lastUpdate}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tab Content: Status de Pedidos */}
        {activeTab === 'status' && (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2" style={{ color: colors.primaza.secondary }}>
                  <List className="h-5 w-5" />
                  Gerenciamento de Status
                </CardTitle>
                {!showStatusForm && (
                  <Button
                    onClick={() => setShowStatusForm(true)}
                    style={{ backgroundColor: colors.primaza.primary }}
                  >
                    <Plus size={16} className="mr-2" />
                    Novo Status
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* Formulário de Status */}
              {showStatusForm && (
                <div className="mb-6 p-4 border rounded-lg bg-gray-50 space-y-4">
                  <h3 className="font-semibold">
                    {editingStatus ? 'Editar Status' : 'Novo Status'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Nome do Status *</Label>
                      <Input
                        value={statusForm.nome}
                        onChange={(e) => setStatusForm({ ...statusForm, nome: e.target.value })}
                        placeholder="Ex: EM ROTA DE ENTREGA"
                      />
                    </div>
                    
                    <div>
                      <Label>Cor (Hex) *</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={statusForm.cor}
                          onChange={(e) => setStatusForm({ ...statusForm, cor: e.target.value })}
                          className="w-20"
                        />
                        <Input
                          type="text"
                          value={statusForm.cor}
                          onChange={(e) => setStatusForm({ ...statusForm, cor: e.target.value })}
                          placeholder="#6B7280"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label>Ordem de Exibição *</Label>
                      <Input
                        type="number"
                        value={statusForm.ordem}
                        onChange={(e) => setStatusForm({ ...statusForm, ordem: parseInt(e.target.value) || 0 })}
                        min="0"
                      />
                    </div>
                    
                    <div className="space-y-2 pt-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="status_ativo"
                          checked={statusForm.ativo}
                          onChange={(e) => setStatusForm({ ...statusForm, ativo: e.target.checked })}
                          className="w-4 h-4"
                        />
                        <Label htmlFor="status_ativo" className="cursor-pointer">Status Ativo</Label>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="status_final"
                          checked={statusForm.is_final}
                          onChange={(e) => setStatusForm({ ...statusForm, is_final: e.target.checked })}
                          className="w-4 h-4"
                        />
                        <Label htmlFor="status_final" className="cursor-pointer">Status Final (Ex: Entregue)</Label>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSaveStatus}
                      disabled={loadingStatus || !statusForm.nome.trim()}
                      style={{ backgroundColor: colors.primaza.primary }}
                    >
                      {loadingStatus ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Save size={16} className="mr-2" />
                      )}
                      {editingStatus ? 'Atualizar' : 'Criar'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowStatusForm(false);
                        setEditingStatus(null);
                        setStatusForm({ nome: '', cor: '#6B7280', ordem: 0, ativo: true, is_final: false });
                      }}
                    >
                      <X size={16} className="mr-2" />
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}

              {/* Lista de Status */}
              {loadingStatus && !showStatusForm ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : statusList.length === 0 ? (
                <p className="text-center py-8 text-gray-500">Nenhum status cadastrado</p>
              ) : (
                <div className="space-y-2">
                  {statusList.map((status) => (
                    <div
                      key={status.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded"
                          style={{ backgroundColor: status.cor }}
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{status.nome}</span>
                            {status.is_final && (
                              <Badge variant="outline" className="text-xs">
                                <CheckCircle size={12} className="mr-1" />
                                Final
                              </Badge>
                            )}
                            {!status.ativo && (
                              <Badge variant="outline" className="text-xs text-gray-500">
                                Inativo
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">Ordem: {status.ordem}</span>
                        </div>
                      </div>
                      
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditStatus(status)}
                        >
                          <Edit size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteStatus(status.id)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tab Content: Usuários */}
        {activeTab === 'usuarios' && (
          <div className="space-y-6">
            {/* Cards de Resumo */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card 
                className={`cursor-pointer transition-all ${filtroRole === 'TODOS' ? 'ring-2 ring-blue-500' : 'hover:shadow-md'}`}
                onClick={() => setFiltroRole('TODOS')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total</p>
                      <p className="text-2xl font-bold text-gray-900">{usuarios.length}</p>
                    </div>
                    <Users className="h-8 w-8 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
              
              <Card 
                className={`cursor-pointer transition-all ${filtroRole === 'admin' ? 'ring-2 ring-red-500' : 'hover:shadow-md'}`}
                onClick={() => setFiltroRole('admin')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Admins</p>
                      <p className="text-2xl font-bold text-red-600">{contarPorRole('admin')}</p>
                    </div>
                    <div className="h-8 w-8 bg-red-100 rounded-full flex items-center justify-center">
                      <span className="text-red-600 font-bold">A</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card 
                className={`cursor-pointer transition-all ${filtroRole === 'lancador' ? 'ring-2 ring-purple-500' : 'hover:shadow-md'}`}
                onClick={() => setFiltroRole('lancador')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Lançadores</p>
                      <p className="text-2xl font-bold text-purple-600">{contarPorRole('lancador')}</p>
                    </div>
                    <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-purple-600 font-bold">L</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card 
                className={`cursor-pointer transition-all ${filtroRole === 'vendedor' ? 'ring-2 ring-green-500' : 'hover:shadow-md'}`}
                onClick={() => setFiltroRole('vendedor')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Vendedores</p>
                      <p className="text-2xl font-bold text-green-600">{contarPorRole('vendedor')}</p>
                    </div>
                    <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 font-bold">V</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Lista de Usuários */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2" style={{ color: colors.primaza.secondary }}>
                    <Users className="h-5 w-5" />
                    Gerenciamento de Usuários
                  </CardTitle>
                  <div className="flex gap-2">
                    {filtroRole !== 'TODOS' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setFiltroRole('TODOS')}
                        className="text-gray-500"
                      >
                        <X size={16} className="mr-1" />
                        Limpar filtro
                      </Button>
                    )}
                    {!showUserForm && (
                      <Button
                        onClick={() => setShowUserForm(true)}
                        style={{ backgroundColor: colors.primaza.primary }}
                      >
                        <UserPlus size={16} className="mr-2" />
                        Novo Usuário
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Formulário de Usuário */}
                {showUserForm && (
                  <div className="mb-6 p-4 border rounded-lg bg-gray-50 space-y-4">
                    <h3 className="font-semibold">
                      {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Nome de Usuário (Login) *</Label>
                        <Input
                          value={userForm.username}
                          onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                          placeholder="ana.paula"
                        />
                      </div>
                      <div>
                        <Label>Nome Completo *</Label>
                        <Input
                          value={userForm.name}
                          onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                          placeholder="Ana Paula Silva"
                        />
                      </div>
                      <div>
                        <Label>{editingUser ? 'Nova Senha (deixe vazio para manter)' : 'Senha *'}</Label>
                        <Input
                          type="password"
                          value={userForm.password}
                          onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                          placeholder="••••••••"
                        />
                      </div>
                      <div>
                        <Label>Perfil *</Label>
                        <select 
                          value={userForm.role}
                          onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                          className="w-full px-3 py-2 border rounded-md"
                        >
                          <option value="vendedor">Vendedor</option>
                          <option value="lancador">Lançador</option>
                          <option value="admin">Administrador</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={handleSaveUser}
                        disabled={loadingUsers || !userForm.username.trim() || !userForm.name.trim()}
                        style={{ backgroundColor: colors.primaza.primary }}
                      >
                        {loadingUsers ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Save size={16} className="mr-2" />
                        )}
                        {editingUser ? 'Atualizar' : 'Criar'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowUserForm(false);
                          setEditingUser(null);
                          setUserForm({ username: '', name: '', password: '', role: 'vendedor' });
                        }}
                      >
                        <X size={16} className="mr-2" />
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}

                {/* Campo de Busca */}
                <div className="mb-4">
                  <Input
                    placeholder="🔍 Buscar por nome ou usuário..."
                    value={buscaUser}
                    onChange={(e) => setBuscaUser(e.target.value)}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {usuariosFiltrados.length} de {usuarios.length} usuários
                    {filtroRole !== 'TODOS' && ` (filtro: ${filtroRole})`}
                  </p>
                </div>

                {/* Lista */}
                {loadingUsers ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                ) : usuariosFiltrados.length === 0 ? (
                  <p className="text-center py-8 text-gray-500">Nenhum usuário encontrado</p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {usuariosFiltrados.map((usuario) => (
                      <div
                        key={usuario.id}
                        className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{usuario.name}</span>
                            {getRoleBadge(usuario.role)}
                          </div>
                          <div className="text-sm text-gray-500">
                            Usuário: <code className="bg-gray-100 px-1 rounded">{usuario.username}</code>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copiarCredenciais(usuario)}
                            style={{ borderColor: colors.primaza.primary }}
                          >
                            {copiado === usuario.id ? (
                              <>
                                <CheckCircle size={14} className="mr-1 text-green-600" />
                                <span className="text-green-600 text-xs">Copiado!</span>
                              </>
                            ) : (
                              <>
                                <Copy size={14} className="mr-1" style={{ color: colors.primaza.primary }} />
                                <span className="text-xs" style={{ color: colors.primaza.primary }}>Copiar Login</span>
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditUser(usuario)}
                          >
                            <Edit size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteUser(usuario.id, usuario.name)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tab Content: Backup */}
        {activeTab === 'backup' && (
          <div className="space-y-6">
            {/* Alertas */}
            {backupSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
                <CheckCircle size={20} />
                {backupSuccess}
              </div>
            )}
            {backupError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                <AlertTriangle size={20} />
                {backupError}
              </div>
            )}

            {/* Card Principal de Backup */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Backup do Banco de Dados
                </CardTitle>
                <CardDescription>
                  Faça backup dos seus dados para manter uma cópia de segurança
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Informações do Banco */}
                {loadingBackup ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                ) : backupInfo && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-blue-600 mb-2">
                        <HardDrive size={20} />
                        <span className="font-medium">Banco de Dados</span>
                      </div>
                      <p className="text-2xl font-bold text-blue-700">{backupInfo.database}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-green-600 mb-2">
                        <FileJson size={20} />
                        <span className="font-medium">Coleções</span>
                      </div>
                      <p className="text-2xl font-bold text-green-700">{backupInfo.collections?.length || 0}</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-purple-600 mb-2">
                        <List size={20} />
                        <span className="font-medium">Total de Documentos</span>
                      </div>
                      <p className="text-2xl font-bold text-purple-700">{backupInfo.total_documents?.toLocaleString('pt-BR') || 0}</p>
                    </div>
                  </div>
                )}

                {/* Detalhes das Coleções */}
                {backupInfo && backupInfo.collections && (
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <FileJson size={16} />
                      Coleções no Banco
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {backupInfo.collections.map((col, idx) => (
                        <div key={idx} className="bg-gray-50 rounded px-3 py-2 text-sm">
                          <span className="font-medium text-gray-700">{col.name}</span>
                          <span className="text-gray-500 ml-2">({col.count})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Botões de Ação */}
                <div className="flex flex-col gap-4 pt-4 border-t">
                  {downloadingBackup && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>{backupStatusMsg}</span>
                        <span>{backupProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="h-3 rounded-full transition-all duration-500"
                          style={{ width: `${backupProgress}%`, backgroundColor: colors.primaza.primary }}
                        />
                      </div>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-4">
                  <Button
                    data-testid="download-backup-btn"
                    onClick={handleDownloadBackup}
                    disabled={downloadingBackup}
                    style={{ backgroundColor: colors.primaza.primary }}
                    className="text-white"
                  >
                    {downloadingBackup ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Gerando Backup...
                      </>
                    ) : (
                      <>
                        <Download className="mr-2 h-4 w-4" />
                        Baixar Backup Completo
                      </>
                    )}
                  </Button>

                  <Button
                    data-testid="backup-direto-btn"
                    onClick={handleBackupDireto}
                    disabled={downloadingBackup}
                    variant="outline"
                    className="border-green-300 text-green-700 hover:bg-green-50"
                    title="Alternativa que gera e baixa o backup de uma vez, sem sistema de fila"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Backup Direto
                  </Button>
                  
                  <div className="relative">
                    <input
                      type="file"
                      accept=".json,.zip"
                      onChange={handleRestoreBackup}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={restoringBackup}
                    />
                    <Button
                      variant="outline"
                      disabled={restoringBackup}
                      className="border-orange-300 text-orange-600 hover:bg-orange-50"
                    >
                      {restoringBackup ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Restaurando...
                        </>
                      ) : (
                        <>
                          <Upload className="mr-2 h-4 w-4" />
                          Restaurar Backup
                        </>
                      )}
                    </Button>
                  </div>
                  
                  <Button
                    variant="ghost"
                    onClick={loadBackupInfo}
                    disabled={loadingBackup}
                  >
                    <RefreshCw className={`mr-2 h-4 w-4 ${loadingBackup ? 'animate-spin' : ''}`} />
                    Atualizar
                  </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Histórico de Backups */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Histórico de Backups
                </CardTitle>
              </CardHeader>
              <CardContent>
                {backupHistory.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Database className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>Nenhum backup realizado ainda</p>
                    <p className="text-sm">Clique em "Baixar Backup" para criar seu primeiro backup</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {backupHistory.map((backup, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="bg-blue-100 p-2 rounded-lg">
                            <FileJson className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{backup.file_name}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span>{new Date(backup.created_at).toLocaleString('pt-BR')}</span>
                              <span>•</span>
                              <span>Por: {backup.created_by}</span>
                              <span>•</span>
                              <span>{formatFileSize(backup.file_size)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-medium text-gray-600">
                            {backup.total_documents?.toLocaleString('pt-BR')} docs
                          </span>
                          <p className="text-xs text-gray-400">
                            {backup.collections?.length} coleções
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Aviso sobre Google Drive */}
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <HardDrive className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-blue-800">Backup Automático para Google Drive</h4>
                    <p className="text-sm text-blue-600 mt-1">
                      Em breve você poderá configurar backup automático diário para o Google Drive.
                      Assim seus dados estarão sempre seguros na nuvem!
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tab Content: Caixas Padrão */}
        {activeTab === 'caixas' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Caixas Padrão
                </CardTitle>
                <div className="flex gap-2 flex-wrap">
                  {caixasPadrao.length === 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSeedCaixas}
                      className="text-blue-600 border-blue-300 hover:bg-blue-50"
                    >
                      <Download size={16} className="mr-2" />
                      Carregar Padrões
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadCaixasPadrao}
                    disabled={loadingCaixas}
                  >
                    <RefreshCw size={16} className={`mr-2 ${loadingCaixas ? 'animate-spin' : ''}`} />
                    Atualizar
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      setShowCaixaForm(true);
                      setEditingCaixa(null);
                      setCaixaForm({ nome: '', comprimento: '', largura: '', altura: '' });
                    }}
                    style={{ backgroundColor: colors.primaza.primary }}
                  >
                    <Plus size={16} className="mr-2" />
                    Nova Caixa
                  </Button>
                </div>
              </div>
              <CardDescription>
                Cadastre tamanhos de caixas padrão para facilitar a cotação operacional. 
                Medidas em <strong>centímetros</strong>.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Formulário de Caixa */}
              {showCaixaForm && (
                <div className="mb-6 p-4 border rounded-lg bg-gray-50">
                  <h3 className="font-medium mb-4 flex items-center gap-2">
                    <Package size={18} />
                    {editingCaixa ? 'Editar Caixa' : 'Nova Caixa'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div className="md:col-span-2">
                      <Label htmlFor="caixaNome">Nome/Descrição</Label>
                      <Input
                        id="caixaNome"
                        placeholder="Ex: CAIXA PAPELÃO 01"
                        value={caixaForm.nome}
                        onChange={(e) => setCaixaForm({ ...caixaForm, nome: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="caixaComp">Comprimento (cm)</Label>
                      <Input
                        id="caixaComp"
                        type="number"
                        placeholder="33"
                        value={caixaForm.comprimento}
                        onChange={(e) => setCaixaForm({ ...caixaForm, comprimento: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="caixaLarg">Largura (cm)</Label>
                      <Input
                        id="caixaLarg"
                        type="number"
                        placeholder="22"
                        value={caixaForm.largura}
                        onChange={(e) => setCaixaForm({ ...caixaForm, largura: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="caixaAlt">Altura (cm)</Label>
                      <Input
                        id="caixaAlt"
                        type="number"
                        placeholder="24"
                        value={caixaForm.altura}
                        onChange={(e) => setCaixaForm({ ...caixaForm, altura: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button
                      onClick={handleSaveCaixa}
                      disabled={savingCaixa || !caixaForm.nome.trim() || !caixaForm.comprimento || !caixaForm.largura || !caixaForm.altura}
                      style={{ backgroundColor: colors.primaza.primary }}
                    >
                      {savingCaixa ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      {editingCaixa ? 'Atualizar' : 'Salvar'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowCaixaForm(false);
                        setEditingCaixa(null);
                        setCaixaForm({ nome: '', comprimento: '', largura: '', altura: '' });
                      }}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}

              {/* Lista de Caixas */}
              {loadingCaixas ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : caixasPadrao.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>Nenhuma caixa cadastrada</p>
                  <p className="text-sm mt-1">
                    Clique em "Carregar Padrões" para importar as caixas padrão ou adicione manualmente
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b" style={{ backgroundColor: colors.primaza.primaryLight }}>
                        <th className="text-left p-3 font-medium" style={{ color: colors.primaza.primary }}>Nome</th>
                        <th className="text-center p-3 font-medium" style={{ color: colors.primaza.primary }}>Comp. (cm)</th>
                        <th className="text-center p-3 font-medium" style={{ color: colors.primaza.primary }}>Larg. (cm)</th>
                        <th className="text-center p-3 font-medium" style={{ color: colors.primaza.primary }}>Alt. (cm)</th>
                        <th className="text-center p-3 font-medium" style={{ color: colors.primaza.primary }}>Cubagem (m³)</th>
                        <th className="text-center p-3 font-medium" style={{ color: colors.primaza.primary }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {caixasPadrao.map((caixa) => {
                        const cubagem = (caixa.comprimento * caixa.largura * caixa.altura) / 1000000;
                        return (
                          <tr key={caixa.id} className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium">{caixa.nome}</td>
                            <td className="p-3 text-center">{caixa.comprimento}</td>
                            <td className="p-3 text-center">{caixa.largura}</td>
                            <td className="p-3 text-center">{caixa.altura}</td>
                            <td className="p-3 text-center font-mono text-xs" style={{ color: colors.primaza.primary }}>
                              {cubagem.toFixed(4)}
                            </td>
                            <td className="p-3 text-center">
                              <div className="flex justify-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setEditingCaixa(caixa.id);
                                    setCaixaForm({
                                      nome: caixa.nome,
                                      comprimento: caixa.comprimento.toString(),
                                      largura: caixa.largura.toString(),
                                      altura: caixa.altura.toString()
                                    });
                                    setShowCaixaForm(true);
                                  }}
                                  className="text-blue-600 hover:bg-blue-50"
                                >
                                  <Edit size={16} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteCaixa(caixa.id)}
                                  className="text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ConfiguracoesUnificadas;
